import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { AppInsightsService } from 'src/lib/tcs-app-insights/app-insights-initializer.service';
import { createSafeProps } from 'src/lib/tcs-app-insights/app-insights-util';
import { getInnermostChildOrSelf, getRoutePath } from 'src/util/angular/ng-router-helpers';
import { filterNull } from 'src/util/rxjs/operators/filter-null';

export interface PageView {
  title: string;
  url: string;
  route: string;
  startRoute: string;
  startUrl: string;
  startTime: number;
  endTime: number;
  durationInMs: number;
  firstLoad: boolean;
  queryParams: Params;
  params: Params;
  navigationStart: NavigationStart;
  navigationEnd: NavigationEnd;
}

@Injectable({ providedIn: 'root' })
export class PageViewService implements OnDestroy {
  private static initialized = false;
  private _onDestroy$ = new Subject();
  private readonly _pageviews$ = new ReplaySubject<PageView>(1);
  public get pageviews$(): Observable<PageView> {
    return this._pageviews$.asObservable();
  }
  constructor(
    private _router: Router,
    activatedRoute: ActivatedRoute,
    private _logger: Logger,
    private _appInsightsService: AppInsightsService,
  ) {
    const pageviews$ = _router.events.pipe(
      map((e) => {
        if (e instanceof NavigationStart) {
          const startTime = Math.round(performance.now());
          const startRoute = getRoutePath(activatedRoute.snapshot);
          return { navigationStart: e, startUrl: window.location.href, startTime, startRoute };
        }
        return undefined;
      }),
      filterNull(),
      switchMap(({ navigationStart, startRoute, startUrl, startTime }) => {
        return this._router.events.pipe(
          filter((e) => e instanceof NavigationEnd && e.id === navigationStart.id),
          map((e) => e as NavigationEnd),
          take(1),
          map((navigationEnd) => {
            const endTime = Math.round(performance.now());
            const firstLoad = navigationEnd.id === 1;
            const endRouteSnapshot = getInnermostChildOrSelf(activatedRoute.snapshot);
            const endRoute = getRoutePath(activatedRoute.snapshot);
            const durationInMs = Math.round(endTime - startTime);
            const params = activatedRoute.snapshot.pathFromRoot
              .map((r) => r.params)
              .reduce((prev, next) => {
                return {
                  ...prev,
                  ...next,
                };
              }, {});

            const queryParams = endRouteSnapshot.pathFromRoot
              .map((r) => r.queryParams)
              .reduce((prev, next) => {
                return {
                  ...prev,
                  ...next,
                };
              }, {});

            let title: string;
            const routeTitle = endRouteSnapshot.data.title;
            if (routeTitle != null) {
              title = routeTitle;
            } else {
              title = '';
            }
            const url = window.location.href;
            const pv: PageView = {
              title,
              url,
              startTime,
              endTime,
              durationInMs,
              firstLoad,
              route: endRoute,
              startRoute,
              startUrl,
              queryParams,
              params,
              navigationStart,
              navigationEnd,
            };
            return pv;
          }),
        );
      }),
    );

    pageviews$.pipe(takeUntil(this._onDestroy$)).subscribe(this._pageviews$);
  }

  public init() {
    if (!PageViewService.initialized) {
      this.pageviews$.pipe(takeUntil(this._onDestroy$)).subscribe((pageView) => {
        this._appInsightsService.appInsights?.trackPageView({
          name: pageView.route,
          uri: pageView.url,
          properties: { duration: pageView.durationInMs, ...createSafeProps(pageView) },
        });
      });
      PageViewService.initialized = true;
    } else {
      this._logger.warning('PageViewService has already been initialized');
    }
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
